class TooltipTrigger extends HTMLElement {
  // Static registry to share state between triggers with same tooltip-id
  static instances = new Map();

  static getOrCreateState(id) {
    if (!TooltipTrigger.instances.has(id)) {
      TooltipTrigger.instances.set(id, {
        open: false,
        activeTrigger: null,
        triggers: new Set(),
        container: null
      });
    }
    return TooltipTrigger.instances.get(id);
  }

  constructor() {
    super();
    this.onClickBound = this.onClick.bind(this);
    this.onBodyClickBound = this.onBodyClick.bind(this);
    this.onWindowScrollBound = this.onWindowScroll.bind(this);
  }

  connectedCallback() {
    this.tooltipId = this.getAttribute("tooltip-id");
    if (!this.tooltipId) {
      console.warn("tooltip-trigger: missing tooltip-id attribute");
      return;
    }

    this.state = TooltipTrigger.getOrCreateState(this.tooltipId);
    this.state.triggers.add(this);

    // Find the container if not already found
    if (!this.state.container) {
      this.state.container = document.querySelector(`[data-tooltip-container="${this.tooltipId}"]`);
    }

    this.addEventListener("click", this.onClickBound);
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.onClickBound);

    if (this.state) {
      this.state.triggers.delete(this);

      // Clean up global listeners if this was the active trigger
      if (this.state.activeTrigger === this) {
        this.close();
      }

      // Clean up registry if no more triggers
      if (this.state.triggers.size === 0) {
        TooltipTrigger.instances.delete(this.tooltipId);
      }
    }
  }

  onClick(e) {
    e.stopPropagation();

    if (this.state.open) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    const { container } = this.state;
    if (!container) {
      console.warn(`tooltip-trigger: container not found for id "${this.tooltipId}"`);
      return;
    }

    // Close any other open tooltips
    TooltipTrigger.instances.forEach((state, id) => {
      if (id !== this.tooltipId && state.open) {
        state.triggers.forEach((trigger) => trigger.close());
      }
    });

    this.state.open = true;
    this.state.activeTrigger = this;

    // Add open classes
    this.state.triggers.forEach((trigger) => trigger.classList.add("tooltip-open"));
    container.classList.add("tooltip-open");
    document.body.classList.add("tooltip-open");

    const closestProductCard = container.closest(".product-item");
    if (closestProductCard) {
      closestProductCard.classList.add("product-item-tooltip-open");
    }

    // Position and add global listeners
    this.positionTooltip();
    document.body.addEventListener("click", this.onBodyClickBound);
    window.addEventListener("scroll", this.onWindowScrollBound);
  }

  close() {
    const { container } = this.state;
    if (!container) return;

    this.state.open = false;
    this.state.activeTrigger = null;

    // Remove open classes
    this.state.triggers.forEach((trigger) => trigger.classList.remove("tooltip-open"));
    container.classList.remove("tooltip-open");
    document.body.classList.remove("tooltip-open");

    const closestProductCard = container.closest(".product-item");
    if (closestProductCard) {
      closestProductCard.classList.remove("product-item-tooltip-open");
    }

    // Remove global listeners
    document.body.removeEventListener("click", this.onBodyClickBound);
    window.removeEventListener("scroll", this.onWindowScrollBound);
  }

  onBodyClick(e) {
    const { container } = this.state;

    // Check if click is on any trigger for this tooltip
    const clickedOnTrigger = Array.from(this.state.triggers).some(
      (trigger) => trigger === e.target || trigger.contains(e.target)
    );
    if (clickedOnTrigger) return;

    // Check if click is inside the container
    if (container && container.contains(e.target)) return;

    this.close();
  }

  onWindowScroll() {
    this.close();
  }

  positionTooltip() {
    const { container, activeTrigger } = this.state;
    if (!container || !activeTrigger) return;

    const triggerRect = activeTrigger.getBoundingClientRect();

    container.style.left = "0px";
    container.style.top = "0px";

    const containerRect = container.getBoundingClientRect();
    const gap = 8;
    const padding = 12;

    // Get sticky header height (0 if not set)
    const stickyHeaderHeight = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue("--header-desktop-sticky-height") || "0"
    );

    const maxLeft = window.innerWidth - containerRect.width - padding;
    const left = Math.max(
      padding,
      Math.min(triggerRect.left + triggerRect.width / 2 - containerRect.width / 2, maxLeft)
    );

    const belowTop = triggerRect.bottom + gap;
    const aboveTop = triggerRect.top - containerRect.height - gap;

    const preferAbove = activeTrigger.getAttribute("tooltip-prefer") === "above";

    let top;
    if (preferAbove) {
      // Prefer above, fall back to below if it doesn't fit (accounting for sticky header)
      top = aboveTop >= stickyHeaderHeight ? aboveTop : belowTop;
    } else {
      // Prefer below, fall back to above if it doesn't fit
      top = belowTop + containerRect.height <= window.innerHeight ? belowTop : aboveTop;
    }

    container.style.left = `${left}px`;
    container.style.top = `${top}px`;
  }
}

customElements.define("tooltip-trigger", TooltipTrigger);
